import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Copy, RefreshCw, Clock, Mail, Loader2, ChevronDown, ChevronRight, MessageSquareHeart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nz = (iso, opts = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) => iso ? new Date(iso).toLocaleString('en-NZ', { ...opts, timeZone: 'Pacific/Auckland' }) : '—';

const STATE = {
  waiting: ['bg-amber-100 text-amber-800', 'password sent · not signed in'],
  active: ['bg-emerald-100 text-emerald-800', 'in their hour'],
  expired: ['bg-gray-200 text-gray-700', 'hour over'],
  failed: ['bg-red-100 text-red-800', 'failed'],
  'setting up': ['bg-sky-100 text-sky-800', 'setting up'],
};

const Tile = ({ label, c }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`funnel-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <div className="mt-2 grid grid-cols-4 gap-2 text-center">
      {[['Visitors', c.visitors], ['Instagram', c.instagram], ['Requests', c.requests], ['Signed in', c.logins]].map(([k, v]) => (
        <div key={k}><p className="text-2xl font-semibold text-[#1B5E20]">{v}</p><p className="text-[10px] text-gray-500">{k}</p></div>
      ))}
    </div>
  </div>
);

export default function TrialFunnel() {
  const { token, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/platform/trial-funnel`, { headers: getAuthHeader() })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load trial funnel'); }
  }, [getAuthHeader]);
  useEffect(() => { if (token) load(); }, [token, load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };
  const extend = (r, hours) => act(`ext-${r.id}-${hours}`, () => axios.post(`${API}/platform/trial-requests/${r.id}/extend`, { hours }, { headers: getAuthHeader() }), `${r.store_name}: +${hours}h`);
  const resend = (r) => act(`re-${r.id}`, async () => {
    const res = await axios.post(`${API}/platform/trial-requests/${r.id}/resend`, {}, { headers: getAuthHeader() });
    toast.success(`New login emailed to ${r.email} (you're BCC'd) — password ${res.data.temporary_password}`);
  });
  const followUp = (r) => act(`fu-${r.id}`, () => axios.post(`${API}/platform/trial-requests/${r.id}/follow-up`, {}, { headers: getAuthHeader() }), `"How did it go?" emailed to ${r.email} (you're BCC'd)`);
  const copyLink = () => { navigator.clipboard?.writeText(data.ad_link); toast.success('Ad link copied'); };

  if (!data) return null;
  const max = Math.max(1, ...data.series.map(d => d.visitors));

  return (
    <div className="bg-[#FAFDF7] border border-[#C8E6C9] rounded-2xl p-4 md:p-5 mb-8" data-testid="trial-funnel">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="trial-funnel-toggle">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#1B5E20]" />
          <h2 className="text-lg font-semibold text-[#1B5E20]">Trial page & Instagram ad</h2>
          <span className="text-xs text-gray-500 hidden sm:inline">today: {data.today.visitors} visitors · {data.today.requests} requests · {data.today.logins} signed in</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm bg-white border border-gray-200 rounded-xl px-3 py-2">
            <span className="text-gray-500">Use this link in the ad:</span>
            <code className="font-mono text-[#1B5E20] text-xs sm:text-sm" data-testid="ad-link">{data.ad_link}</code>
            <Button size="sm" variant="outline" onClick={copyLink} className="rounded-full h-7"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <span className="text-xs text-gray-400 ml-auto">sign-ups email {data.notify_email} · 7pm summary daily · prospects contact {data.contact_email}</span>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <Tile label="Today" c={data.today} /><Tile label="Last 7 days" c={data.week} /><Tile label="All time" c={data.all} />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">Last 14 days — visitors (green) · requests (amber) · signed in (dark)</p>
            <div className="flex items-end gap-1.5 h-24" data-testid="funnel-series">
              {data.series.map(d => (
                <div key={d.day} className="flex-1 flex flex-col justify-end items-center gap-0.5" title={`${d.day}: ${d.visitors} visitors (${d.instagram} IG), ${d.requests} requests, ${d.logins} signed in`}>
                  <div className="w-full flex items-end gap-px">
                    <div className="flex-1 bg-[#A5D6A7] rounded-t" style={{ height: `${(d.visitors / max) * 80}px` }} />
                    <div className="flex-1 bg-amber-400 rounded-t" style={{ height: `${(d.requests / max) * 80}px` }} />
                    <div className="flex-1 bg-[#1B5E20] rounded-t" style={{ height: `${(d.logins / max) * 80}px` }} />
                  </div>
                  <span className="text-[9px] text-gray-400">{d.day.slice(8)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Trial requests</p>
              <Button size="sm" variant="ghost" onClick={load} className="h-7"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            </div>
            {data.requests.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500" data-testid="no-trial-requests">No one has requested a trial yet. Once the ad is live, every request appears here within seconds.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.requests.map(r => {
                  const [cls, label] = STATE[r.state] || STATE.waiting;
                  return (
                    <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm" data-testid={`trial-request-${r.id}`}>
                      <div className="min-w-[200px]">
                        <p className="font-medium text-gray-900">{r.store_name} <span className="text-gray-500 font-normal">· {r.name}</span></p>
                        <p className="text-xs text-gray-500">{r.email} · via {r.source} · {nz(r.requested_at)}</p>
                      </div>
                      <Badge className={`${cls} hover:${cls.split(' ')[0]}`} data-testid="trial-request-state">{label}</Badge>
                      <span className="text-xs text-gray-500">
                        {r.first_login_at ? `signed in ${nz(r.first_login_at)} · ends ${nz(r.trial_ends_at)}` : 'not signed in yet'}
                        {r.extended_hours ? ` · +${r.extended_hours}h given` : ''}{r.resent_count ? ` · resent ×${r.resent_count}` : ''}
                        {r.follow_up_sent_at ? (r.follow_up_sent_at === 'converted' ? ' · became a customer' : ` · follow-up sent ${nz(r.follow_up_sent_at)}`) : ''}
                      </span>
                      {r.state !== 'failed' && (
                        <div className="ml-auto flex items-center gap-1.5">
                          <Button size="sm" variant="outline" className="h-7 rounded-full" disabled={!!busy} onClick={() => extend(r, 1)} data-testid="trial-extend-1h">
                            {busy === `ext-${r.id}-1` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3 mr-1" />} +1h
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 rounded-full" disabled={!!busy} onClick={() => extend(r, 24)} data-testid="trial-extend-24h">
                            {busy === `ext-${r.id}-24` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3 mr-1" />} +24h
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 rounded-full" disabled={!!busy} onClick={() => resend(r)} data-testid="trial-resend">
                            {busy === `re-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />} Resend login
                          </Button>
                          {r.first_login_at && !r.follow_up_sent_at && (
                            <Button size="sm" variant="outline" className="h-7 rounded-full border-[#1B5E20]/40 text-[#1B5E20]" disabled={!!busy} onClick={() => followUp(r)} data-testid="trial-follow-up">
                              {busy === `fu-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquareHeart className="w-3 h-3 mr-1" />} How did it go?
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
